import { cn } from "@/lib/utils"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { AlertTriangle, Check, X } from "lucide-react"
import { Section, Field, ColorField, ToggleField } from "../../shared/components/EditorFields"
import { ImageField } from "../../shared/components/ImageField"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { describeLanguage, type LanguageInfo } from "../../shared/languages"
import { isSiteLocale } from "../../shared/siteLanguages"

/** A row of toggleable id chips. Used for every Site scope dimension. */
function ChipPicker({
  options, selected, onToggle, emptyText,
}: {
  options: Array<{ id: string; name: string }>
  selected: string[]
  onToggle: (id: string) => void
  emptyText: string
}) {
  /*
   * Selected ids with no matching option — a record deleted since, or left
   * behind by an older data model. They were previously invisible while
   * still counting toward the scope and still being sent downstream, which
   * is how a site ended up claiming 19 countries while showing one. Shown
   * as removable, so the author can actually clear them.
   */
  const known = new Set(options.map(o => o.id))
  const orphans = selected.filter(id => !known.has(id))

  if (options.length === 0 && orphans.length === 0) {
    return <p className="text-xs text-muted-foreground">{emptyText}</p>
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {orphans.map(id => (
        <button
          key={id}
          type="button"
          onClick={() => onToggle(id)}
          title="This record no longer exists. Click to remove it from the scope."
          className="inline-flex items-center gap-1 rounded-full border border-amber-400 bg-amber-50 px-2 py-0.5 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-400"
        >
          <AlertTriangle className="h-3 w-3" />
          <code className="font-mono text-[10px]">{id.slice(0, 8)}…</code>
          <X className="h-3 w-3" />
        </button>
      ))}
      {options.map(o => {
        const on = selected.includes(o.id)
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onToggle(o.id)}
            className={cn(
              "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-colors",
              on ? "border-primary bg-primary/10 text-foreground" : "border-input text-muted-foreground hover:text-foreground",
            )}
          >
            {on && <Check className="h-3 w-3" />}
            {o.name}
          </button>
        )
      })}
    </div>
  )
}


export function SiteFields({
  entity, countries, regions, cities, articleCategories, set, editLocale, defaultLocale, languages,
}: {
  entity: any
  countries: Array<{ id: string; name: string }>
  regions: Array<{ id: string; name: string }>
  cities: Array<{ id: string; name: string }>
  articleCategories: Array<{ id: string; name: string }>
  set: (p: any) => void
  editLocale: string
  defaultLocale: string
  languages: LanguageInfo[]
}) {
  const scope = entity.serviceMap ?? { countryIds: [], regionIds: [], cityIds: [] }
  const articles = entity.articles ?? { allCategories: true, categoryIds: [] }

  // The three geography lists are independent — toggling a city never implies
  // its country (see FIDELITY NOTE 1), so each dimension patches only itself.
  const toggleGeo = (dim: "countryIds" | "regionIds" | "cityIds", id: string) => {
    const cur: string[] = scope[dim] ?? []
    set({ serviceMap: { ...scope, [dim]: cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id] } })
  }
  const toggleCategory = (id: string) => {
    const cur: string[] = articles.categoryIds ?? []
    set({ articles: { ...articles, categoryIds: cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id] } })
  }

  const geoCount =
    (scope.countryIds?.length ?? 0) + (scope.regionIds?.length ?? 0) + (scope.cityIds?.length ?? 0)

  return (
    <>
    <Section title="Site" defaultOpen>
      <Field label="Name" hint="The public-facing site name">
        <Input value={entity.name ?? ""} onChange={e => set({ name: e.target.value })} className="h-8 text-sm" />
      </Field>
      <Field label="Base URL">
        <Input value={entity.baseUrl ?? ""} onChange={e => set({ baseUrl: e.target.value })} placeholder="https://…" className="h-8 text-sm" />
      </Field>
      {/* Overrides the site's bundled logo in the header, footer and splash screen */}
      <ImageField
        label="Logo"
        value={entity.logo}
        onChange={v => set({ logo: v || undefined })}
        hint="Transparent PNG or SVG, roughly 200–400px wide. Renders ~40px tall in the footer and unconstrained in the header."
      />
    </Section>

    <SiteLanguages entity={entity} set={set} languages={languages} fallbackDefault={defaultLocale} />

    <Section title={`Service map scope — ${geoCount || "none"}`}>
      <p className="text-[11px] text-muted-foreground">Which geography this site surfaces. Lists are independent: a city does not require its country.</p>
      {geoCount === 0 && (
        <p className="text-[11px] text-amber-600">
          Nothing scoped — this site surfaces no services.
        </p>
      )}
      <Field label="Countries">
        <ChipPicker
          options={countries} selected={scope.countryIds ?? []}
          onToggle={id => toggleGeo("countryIds", id)}
          emptyText="No countries in the Service Map yet."
        />
      </Field>
      <Field label="Regions">
        <ChipPicker
          options={regions} selected={scope.regionIds ?? []}
          onToggle={id => toggleGeo("regionIds", id)}
          emptyText="No regions in the Service Map yet."
        />
      </Field>
      <Field label="Cities">
        <ChipPicker
          options={cities} selected={scope.cityIds ?? []}
          onToggle={id => toggleGeo("cityIds", id)}
          emptyText="No cities in the Service Map yet."
        />
      </Field>
    </Section>

    <Section title="Articles scope">
      <p className="text-[11px] text-muted-foreground">Which knowledge-base categories this site publishes</p>
      <ToggleField
        label="Publish all categories"
        checked={!!articles.allCategories}
        onChange={v => set({ articles: { ...articles, allCategories: v } })}
      />
      {!articles.allCategories && (
        <Field label="Categories">
          <ChipPicker
            options={articleCategories} selected={articles.categoryIds ?? []}
            onToggle={toggleCategory}
            emptyText="No article categories yet."
          />
        </Field>
      )}
    </Section>


    <Section title="Cookie banner">
      <Field
        label={`Cookie banner — ${editLocale}`}
        hint={editLocale === defaultLocale
          ? "Plain text. This is the source wording other locales fall back to."
          : `Plain text. Leave empty to fall back to ${defaultLocale}.`}
      >
        <Textarea
          value={editLocale === defaultLocale
            ? entity.cookieBanner ?? ""
            : entity.cookieBannerTranslations?.[editLocale] ?? ""}
          placeholder={editLocale === defaultLocale ? "" : entity.cookieBanner ?? ""}
          onChange={e => {
            const v = e.target.value
            if (editLocale === defaultLocale) set({ cookieBanner: v })
            else set({ cookieBannerTranslations: { ...entity.cookieBannerTranslations, [editLocale]: v } })
          }}
          rows={3}
          className="text-sm"
        />
      </Field>
    </Section>

    <Section title="Branding">
      <div className="grid grid-cols-2 gap-2">
        {(["headerBgColor", "headerColor", "headerColorBg", "headerColorTxt"] as const).map(f => (
          <ColorField key={f} label={f.replace(/([A-Z])/g, " $1")} value={entity[f]} onChange={v => set({ [f]: v })} />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {(["pageBgColor", "pageColor", "pageColorBg", "pageColorTxt"] as const).map(f => (
          <ColorField key={f} label={f.replace(/([A-Z])/g, " $1")} value={entity[f]} onChange={v => set({ [f]: v })} />
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground">
        Header nav, footer and page content blocks each have their own row in the browser tree.
      </p>
    </Section>
    </>
  )
}

// ── Header nav ───────────────────────────────────────────────────────────────

// Which of the team's languages this site publishes, and which one it falls back to
function SiteLanguages({ entity, set, languages, fallbackDefault }: {
  entity: any
  set: (p: any) => void
  languages: LanguageInfo[]
  fallbackDefault: string
}) {
  const published: string[] = entity.locales ?? []
  const def: string = entity.defaultLocale || fallbackDefault
  // Codes the site lists that the team doesn't, so they stay visible and removable
  const options = [...languages, ...published.filter(c => !languages.some(l => l.code === c)).map(c => describeLanguage(c))]

  const toggle = (code: string) => {
    if (code === def) return
    // A code the sites can't show can be switched off, never on
    if (!published.includes(code) && !isSiteLocale(code)) return
    set({ locales: published.includes(code) ? published.filter(c => c !== code) : [...published, code] })
  }
  const setDefault = (code: string) =>
    set({ defaultLocale: code, locales: published.includes(code) ? published : [...published, code] })

  return (
    <Section title="Languages">
      <p className="text-[11px] text-muted-foreground">
        The languages readers can choose on this site. They belong to the site, not to a country. Add new ones in Manage languages.
      </p>
      <Field label="Default language" hint="What every other language falls back to">
        <Select value={def} onValueChange={setDefault}>
          <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            {options.filter(l => isSiteLocale(l.code) || l.code === def).map(l => (
              <SelectItem key={l.code} value={l.code} className="text-xs">
                {l.name} <span className="font-mono text-[10px] text-muted-foreground">{l.code}</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>
      <Field label="Languages offered">
        <div className="flex flex-wrap gap-1.5">
          {options.filter(l => isSiteLocale(l.code) || published.includes(l.code)).map(l => {
            const on = published.includes(l.code) || l.code === def
            const supported = isSiteLocale(l.code)
            return (
              <button
                key={l.code}
                type="button"
                onClick={() => toggle(l.code)}
                disabled={l.code === def}
                title={l.code === def ? "The default language is always offered"
                  : !supported ? `The sites can't show "${l.code}": click to stop offering it`
                    : on ? `Stop offering ${l.name}` : `Offer ${l.name}`}
                className={cn(
                  "flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors",
                  !supported ? "border-rose-300 bg-rose-50 text-rose-800 line-through dark:bg-rose-950/30 dark:text-rose-300"
                    : on ? "border-primary/40 bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted",
                  l.code === def && "cursor-default",
                )}
              >
                {on && <Check className="h-3 w-3" />}
                {l.name}
                <span className="font-mono text-[10px] text-muted-foreground">{l.code}</span>
              </button>
            )
          })}
        </div>
      </Field>
    </Section>
  )
}
